import hashStringCyrb53 from '@screens/Map/utils/hashStringCyrb53';
import type { BBox, MultiPolygon, Polygon } from '@turf/turf';
import { area, bbox, bboxPolygon, center, feature, featureCollection } from '@turf/turf';
import { geoJsonViewBox } from '@utils/geoJsonToSvg';
import dayjs from 'dayjs';
import { createEntityAdapter } from 'redux-ngrx-entity';
import { Region } from 'src/state/regions/region.models';
import type { FieldSeason, FieldSeasonData } from '../../core/models/seasons';
import type { Field, RegionDTO, RegionFeatureProperties } from './region.models';

const fieldAdapter = createEntityAdapter<Field>();

export class RegionUtils {
  static regionToCollection(
    region: Region
  ): GeoJSON.FeatureCollection<Polygon | MultiPolygon, RegionFeatureProperties> {
    return featureCollection(
      Object.values(region.fields.entities).map((field) =>
        feature(
          field.geometry,
          {
            center: field.center,
            name: field.name,
            parent_region_id: field.regionId,
            id: field.id,
            activeSeason: field.activeSeason?.season.id ?? 'none',
            inactiveSeasons: field.inactiveSeasons.map((s) => s.season.id),
          },
          {
            id: field.intId,
          }
        )
      )
    );
  }

  static parseRegion(region: RegionDTO, seasons: FieldSeasonData[]): Region {
    const { fields, collection } = region.fields.reduce(
      (acc, curr) => {
        const field = this.parseField(curr, region.id, seasons);
        if (field) {
          acc.fields = fieldAdapter.addOne(field, acc.fields);
          acc.collection.features.push(feature(field.geometry));
        }

        return acc;
      },
      { fields: fieldAdapter.getInitialState(), collection: featureCollection([]) }
    );
    const regionBbox: BBox = collection.features.length ? bbox(collection) : [0, 0, 0, 0];
    const regionCenter = center(bboxPolygon(regionBbox));

    return {
      id: region.id,
      name: region.name,
      area: region.declared_area,
      children: region.children_region.map((item) => item.id),
      fields: fields,
      parent: region.parent_id,
      bbox: regionBbox,
      center: regionCenter.geometry,
      isRoot: false,
    };
  }

  static parseField(field: Field, regionId: string, seasons: FieldSeasonData[]): Field | null {
    const now = dayjs();
    const fieldSeasons = seasons.filter((season) => Boolean(season.fieldsSeason[field.id]));
    const { activeSeason, inactiveSeasons } = fieldSeasons.reduce<{
      activeSeason: FieldSeason | null;
      inactiveSeasons: FieldSeason[];
    }>(
      (acc, season) => {
        const data = season.fieldsSeason[field.id];

        if (
          season.season.active &&
          !acc.activeSeason &&
          dayjs(data.start_date).isBefore(now) &&
          dayjs(data.end_date).isAfter(now)
        ) {
          acc.activeSeason = data;
        } else {
          acc.inactiveSeasons.push(data);
        }

        return acc;
      },
      {
        activeSeason: null,
        inactiveSeasons: [],
      }
    );

    return {
      ...field,
      bbox: bbox(field.geometry),
      center: center(field.geometry),
      intId: hashStringCyrb53(field.id),
      declared_area:
        field.declared_area === 0
          ? Number.parseFloat(field['calculated_area'])
          : field.declared_area,
      regionId,
      activeSeason: activeSeason,
      inactiveSeasons: inactiveSeasons,
      viewBox: geoJsonViewBox(field.geometry),
    };
  }

  static getFieldSeasonIds(field: Field): string[] {
    return [
      ...(field.activeSeason ? [field.activeSeason.season_id] : []),
      ...field.inactiveSeasons.map((s) => s.season_id),
    ];
  }

  static parseRegionTreeToMap(region: RegionDTO, seasons: FieldSeasonData[] = []) {
    const collection: GeoJSON.FeatureCollection<
      Polygon | MultiPolygon,
      RegionFeatureProperties
    > = featureCollection([]);
    const data = new Array<Region>();

    const iterate = (r: RegionDTO) => {
      r.fields = r.fields.filter((field) => field.geometry && area(field.geometry) > 10);

      const rN = RegionUtils.parseRegion(r, seasons);
      data.push(rN);

      // Build collection
      for (const field of Object.values(rN.fields.entities)) {
        collection.features.push(
          feature(
            field.geometry,
            {
              center: field.center,
              name: field.name,
              parent_region_id: field.regionId,
              id: field.id,
              activeSeason: field.activeSeason?.season.id ?? 'none',
              inactiveSeasons: field.inactiveSeasons.map((s) => s.season.id),
            },
            {
              id: field.intId,
            }
          )
        );
      }

      for (const item of r.children_region) {
        iterate(item);
      }
    };

    iterate(region);

    // Clear triplicated data
    region.fields.forEach((field) => {
      field.geometry = { type: 'MultiPolygon', coordinates: [] };
    });

    // Update root center & bbox
    if (data.length) {
      const propertyBbox = bbox(collection);
      const propertyCenter = center(bboxPolygon(propertyBbox));
      data[0].isRoot = true;
      data[0].bbox = propertyBbox;
      data[0].center = propertyCenter.geometry;
    }

    return { data, collection, tree: region };
  }
}
